/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// MartyBlocks class
// Responsible for managing MartyBlocks
// sessions (collecting and storing info)
// Communicating with the analytics db
//
// (C) Robotical 2024
//
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import randomHashGenerator from "../helpers/randomHashGenerator";
import { sessionTitleBuilder, sessionUsedBlocksTitleBuilder, } from "../path-builders/martyblocks-path-builder";
import { Database } from "./Database";
export default class MartyBlocksDatabase extends Database {
    constructor(config, databaseName) {
        super(config, databaseName);
        this.dataToStore = {
            firstTimestamp: "",
            lastTimestamp: "",
            sessionId: "",
            appSessionId: "",
            robotSessionId: "",
            isActive: false,
            usedBlocks: {},
        };
        this.firstTimestamp = null;
        this.lastTimestamp = null;
        this.heartbeat = null;
        this.heartbeatInterval = 60000;
        this.maxIntervalBetweenSessions = 3600000; // 1 hour
        this.ACTIVE_SESSION_TIME_POINT = 300000; // 5 minutes
    }
    // =============== INHERITED METHODS ===============
    // =================================================
    addDataToSession(key, value) {
        if (value !== undefined)
            this.dataToStore[key] = value;
    }
    prepareInitialData(appSessionId, robotSessionId) {
        this.addDataToSession("appSessionId", appSessionId);
        this.addDataToSession("robotSessionId", robotSessionId);
        this.addDataToSession("isActive", false);
        this.addDataToSession("usedBlocks", {});
    }
    async heartbeatHandler() {
        // decide if we should start a new session or
        // we continue
        const newLastTimestamp = new Date();
        this.decideIfActive(newLastTimestamp);
        if (this.lastTimestamp && newLastTimestamp.getTime() - this.lastTimestamp.getTime() > this.maxIntervalBetweenSessions) {
            // the app was inactive long enough
            // removing the old session and starting a new one
            this.stopHeartbeat();
            this.startSession(this.dataToStore["appSessionId"], this.dataToStore["robotSessionId"]);
        }
        else {
            // we are still in the same session, update heartbeat
            this.lastTimestamp = newLastTimestamp;
            const dbPath = sessionTitleBuilder(this.dataToStore["sessionId"]);
            // checking if the dbPath exists in the database, if not then we won't send a new lastTimestamp.
            // This is probably a development artefact
            const pathExist = await this.exists(dbPath);
            if (pathExist) {
                // send heartbeat only if session already exists
                await this.update({ lastTimestamp: this.lastTimestamp }, dbPath);
            }
        }
    }
    async decideIfActive(newLastTimestamp) {
        // if a session is active for more than 1 minute (diff between firstTimestamp and lastTimestamp > 1 minute)
        // then we consider it active
        if (!this.dataToStore["sessionId"])
            return;
        if (this.dataToStore["isActive"])
            return;
        if (!this.firstTimestamp)
            return;
        const diff = newLastTimestamp.getTime() - this.firstTimestamp.getTime();
        if (diff > this.ACTIVE_SESSION_TIME_POINT) {
            this.dataToStore["isActive"] = true;
            await this.update({ isActive: true }, sessionTitleBuilder(this.dataToStore["sessionId"]));
        }
    }
    async setHeartbeat() {
        // every this.heartbeatInterval we sent a new timestamp
        // to the db. In that way we can estimate the time
        // marty was used.
        // Heartbeat is only sent when the app is active.
        // We will take advantage of that and we will use
        // it to decide if we should create a new session
        // or no (if the app was inactive for more than say
        // an hour, then we can categorise the new activity
        // as a new session).
        if (!this.heartbeat) {
            // if there is no heartbeat we just
            // set it up
            this.heartbeat = setInterval(this.heartbeatHandler.bind(this), this.heartbeatInterval);
            this.heartbeatHandler();
        }
    }
    stopHeartbeat() {
        // stopping repeatedly sending msgs to the db
        if (this.heartbeat) {
            clearInterval(this.heartbeat);
        }
        this.lastTimestamp = null;
        this.heartbeat = null;
    }
    async startSession(appSessionId, robotSessionId) {
        if (!this.db)
            await this.initialize();
        // making sure the rest of the app runs
        // even if we are still trying to
        // asynchronously get the device id
        this.prepareInitialData(appSessionId, robotSessionId);
        // The app was just started. Starting session
        const sessionId = randomHashGenerator();
        this.addDataToSession("sessionId", sessionId);
        // safety net: making sure we only post when
        // the appSessionId is set
        if (this.dataToStore["appSessionId"]) {
            this.lastTimestamp = new Date();
            this.firstTimestamp = new Date();
            this.addDataToSession("firstTimestamp", this.firstTimestamp.toISOString());
            this.addDataToSession("lastTimestamp", this.lastTimestamp.toISOString());
            await this.set(this.dataToStore, sessionTitleBuilder(sessionId));
            this.setHeartbeat();
        }
    }
    async addUsedBlock(blockName) {
        if (!this.dataToStore.sessionId)
            return false;
        blockName = this.encodeBlockName(blockName);
        const dbPath = sessionUsedBlocksTitleBuilder(this.dataToStore["sessionId"], blockName);
        const counter = await this.get(dbPath);
        if (counter) {
            return await this.update({ [dbPath]: counter + 1 });
        }
        else {
            return await this.update({ [dbPath]: 1 });
        }
    }
    encodeBlockName(blockName) {
        return blockName.replace(/[.#$[\]]/g, "_");
    }
    endSession() {
        this.stopHeartbeat();
        this.dataToStore = {
            firstTimestamp: "",
            lastTimestamp: "",
            sessionId: "",
            appSessionId: "",
            robotSessionId: "",
            usedBlocks: {},
            isActive: false,
        };
        this.lastTimestamp = null;
        this.firstTimestamp = null;
    }
    async storeRobotSessionId(robotSessionId) {
        if (!this.dataToStore["sessionId"])
            return false;
        this.addDataToSession("robotSessionId", robotSessionId);
        return await this.update({ robotSessionId: robotSessionId }, sessionTitleBuilder(this.dataToStore["sessionId"]));
    }
}
